"use client";

import { Button } from "@/components/ui/button";
import { NumberIncrementer } from "@/components/number-incrementer";
import Link from "next/link";
import { BlockiesAvatar } from "@/components/blockies-avatar";
import { useGetCampaignLeaderboard } from "@/hooks/challenges";
import { CAMPAIGN_CONFIG } from "@/config";
import { LeaderboardPeriodType } from "@/types/challenges.types";
import { ButtonText, Headline6 } from "@/components/typography";

export function LeaderboardSpotlightSection() {
  const { data: leaderboardData } = useGetCampaignLeaderboard(
    CAMPAIGN_CONFIG.id,
    CAMPAIGN_CONFIG.groupId,
    "",
    3,
    LeaderboardPeriodType.ALL,
    0
  );

  const topPlayers = leaderboardData?.data?.slice(0, 3) || [];

  // If no data yet, show loading state or return null
  if (!topPlayers.length) {
    return null;
  }

  const formatName = (name: string | undefined, address: string) => {
    if (name && name.trim()) return name.toUpperCase();
    return `${address.slice(0, 6)}...${address.slice(-4)}`.toUpperCase();
  };

  const formatPoints = (points: string) => {
    return parseInt(points) || 0;
  };

  return (
    <section className="relative w-full bg-background py-12 md:py-16 lg:py-24">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Left Column - Text Content */}
          <div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-[-1.5px] text-spicenet-black mb-4 md:mb-6">
              Leaderboard
              <br />
              Spotlight
            </h2>
            <Headline6 className="text-xs md:text-sm text-spicenet-black leading-relaxed mb-6 md:mb-8 max-w-xl font-mono tracking-[0px]! uppercase tracking-wide">
              See who's leading the way in the Spicenet community. The
              leaderboard highlights the most active members, their
              achievements, and the rewards they've earned. Check your rank,
              challenge the top players, and push higher for even greater
              prizes.
            </Headline6>
            <Link href="/leaderboard">
              <Button variant="primary" size="default">
                <ButtonText>View Full Leaderboard</ButtonText>
              </Button>
            </Link>
          </div>

          {/* Right Column - Leaderboard Podium */}
          <div className="relative">
            {/* Container with corner decorations */}
            <div className="relative border-2 border-spicenet-primary bg-spicenet-primary/10 p-6 md:p-8 lg:p-12">
              {/* Corner decorations */}
              <span className="absolute top-2 left-2 w-2 h-2 md:w-3 md:h-3 bg-spicenet-primary" />
              <span className="absolute top-2 right-2 w-2 h-2 md:w-3 md:h-3 bg-spicenet-primary" />
              <span className="absolute bottom-2 left-2 w-2 h-2 md:w-3 md:h-3 bg-spicenet-primary" />
              <span className="absolute bottom-2 right-2 w-2 h-2 md:w-3 md:h-3 bg-spicenet-primary" />

              {/* Podium Layout */}
              <div className="flex items-end justify-center gap-1 md:gap-2 lg:gap-4">
                {/* #2 - Left (medium height) */}
                {topPlayers[1] && (
                  <div className="flex-1 flex flex-col items-center">
                    <div className="bg-spicenet-primary/40 w-full pt-6 md:pt-8 pb-4 md:pb-6 px-2 md:px-3 flex flex-col items-center relative min-h-[180px] md:min-h-[240px] lg:min-h-[280px]">
                      {/* Rank badge */}
                      <div className="absolute -top-2 md:-top-3 left-1/2 -translate-x-1/2 bg-spicenet-primary text-white text-[10px] md:text-xs font-medium tracking-[0px] px-2 md:px-3 py-0.5 md:py-1 uppercase">
                        #02
                      </div>

                      {/* Avatar */}
                      <div className="mb-2 md:mb-3">
                        <BlockiesAvatar
                          seed={topPlayers[1].address}
                          size={12}
                          scale={3}
                          className="border-2 border-white md:scale-125"
                        />
                      </div>

                      {/* Name */}
                      <p className="text-white font-medium tracking-[-1.5px] text-[10px] md:text-sm lg:text-base uppercase mb-1 text-center break-words max-w-full px-1 line-clamp-2">
                        {formatName(topPlayers[1].name, topPlayers[1].address)}
                      </p>

                      {/* Points */}
                      <NumberIncrementer
                        value={formatPoints(topPlayers[1].totalPoints)}
                        className="text-white text-base md:text-xl lg:text-2xl font-medium tracking-[-1.5px] mb-0.5 md:mb-1"
                        duration={2000}
                      />
                      <p className="text-white/80 text-[10px] md:text-xs uppercase tracking-wider">
                        Points
                      </p>
                    </div>
                  </div>
                )}

                {/* #1 - Center (tallest) */}
                {topPlayers[0] && (
                  <div className="flex-1 flex flex-col items-center">
                    <div className="bg-spicenet-primary w-full pt-6 md:pt-8 pb-4 md:pb-6 px-2 md:px-3 flex flex-col items-center relative min-h-[200px] md:min-h-[280px] lg:min-h-[320px]">
                      {/* Rank badge */}
                      <div className="absolute -top-2 md:-top-3 left-1/2 -translate-x-1/2 bg-white text-spicenet-primary text-[10px] md:text-xs font-medium tracking-[0px] px-2 md:px-3 py-0.5 md:py-1 uppercase">
                        #01
                      </div>

                      {/* Avatar */}
                      <div className="mb-2 md:mb-3">
                        <BlockiesAvatar
                          seed={topPlayers[0].address}
                          size={14}
                          scale={3}
                          className="border-2 border-white md:scale-125"
                        />
                      </div>

                      {/* Name */}
                      <p className="text-white font-medium tracking-[-0.5px] text-[10px] md:text-sm lg:text-base uppercase mb-1 text-center break-words max-w-full px-1 line-clamp-2">
                        {formatName(topPlayers[0].name, topPlayers[0].address)}
                      </p>

                      {/* Points */}
                      <NumberIncrementer
                        value={formatPoints(topPlayers[0].totalPoints)}
                        className="text-white text-lg md:text-xl lg:text-2xl font-medium tracking-[0px] mb-0.5 md:mb-1"
                        duration={2000}
                      />
                      <p className="text-white/80 text-[10px] md:text-xs uppercase tracking-wider">
                        Points
                      </p>
                    </div>
                  </div>
                )}

                {/* #3 - Right (shortest) */}
                {topPlayers[2] && (
                  <div className="flex-1 flex flex-col items-center">
                    <div className="bg-spicenet-primary/30 w-full pt-6 md:pt-8 pb-4 md:pb-6 px-2 md:px-3 flex flex-col items-center relative min-h-[160px] md:min-h-[200px] lg:min-h-[240px]">
                      {/* Rank badge */}
                      <div className="absolute -top-2 md:-top-3 left-1/2 -translate-x-1/2 bg-spicenet-primary text-white text-[10px] md:text-xs font-medium tracking-[0px] px-2 md:px-3 py-0.5 md:py-1 uppercase">
                        #03
                      </div>

                      {/* Avatar */}
                      <div className="mb-2 md:mb-3">
                        <BlockiesAvatar
                          seed={topPlayers[2].address}
                          size={12}
                          scale={3}
                          className="border-2 border-white md:scale-125"
                        />
                      </div>

                      {/* Name */}
                      <p className="text-white font-medium tracking-[-1.5px] text-[10px] md:text-sm lg:text-base uppercase mb-1 text-center break-words max-w-full px-1 line-clamp-2">
                        {formatName(topPlayers[2].name, topPlayers[2].address)}
                      </p>

                      {/* Points */}
                      <NumberIncrementer
                        value={formatPoints(topPlayers[2].totalPoints)}
                        className="text-white text-base md:text-xl lg:text-2xl font-medium tracking-[-1.5px] mb-0.5 md:mb-1"
                        duration={2000}
                      />
                      <p className="text-white/80 text-[10px] md:text-xs uppercase tracking-wider">
                        Points
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Background pattern/dots - optional decorative element */}
              <div className="absolute inset-0 pointer-events-none opacity-10">
                <div className="absolute top-4 right-4 w-32 h-32 bg-[radial-gradient(circle,_#0E0D0B_1px,_transparent_1px)] bg-[length:8px_8px]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
